/**
 * AdminDuplicateRolls — where the same person sits on one roll twice.
 *
 * Nothing is deleted: choose the entry to keep, and the others are marked
 * "merged" and pointed at it, so the roll shows the person once. The offices
 * that came with the duplicates are recorded on the keeper, and a merge can be
 * put back.
 *
 * Route: /admin/duplicate-rolls
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Merge, Undo2, Users, CheckCircle2 } from 'lucide-react';

const MATCHED_ON = {
  email: 'same email address',
  phone: 'same phone number',
  person: 'same person record',
  account: 'same member login',
  name: 'same name and date of birth',
};

const Group = ({ group }) => {
  const qc = useQueryClient();
  const [keep, setKeep] = useState(group.suggested_keep);

  const merge = useMutation({
    mutationFn: async () => (await api.post('/admin/duplicate-rolls/mark-merged', {
      keep_id: keep,
      merge_ids: group.rows.map((r) => r.id).filter((id) => id !== keep),
    })).data,
    onSuccess: (res) => {
      toast.success(`${group.name} now appears once on ${group.church_name}`, {
        description: res.roles_kept.length
          ? `Offices kept on the entry: ${res.roles_kept.join(', ')}`
          : 'The duplicate entries are marked merged — nothing was deleted.',
      });
      qc.invalidateQueries({ queryKey: ['duplicate-rolls'] });
      qc.invalidateQueries({ queryKey: ['duplicate-rolls-merged'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card data-testid={`duplicate-group-${group.church_id}-${group.person_key}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{group.name}</CardTitle>
            <CardDescription>
              {group.count} entries on {group.church_name} ·{' '}
              {MATCHED_ON[group.matched_on] || group.matched_on}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px]">{group.count} entries</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="divide-y rounded-lg border border-slate-200">
          {group.rows.map((row) => (
            <button key={row.id} type="button" onClick={() => setKeep(row.id)}
                    className={`flex w-full flex-wrap items-center gap-3 p-2.5 text-left transition-colors ${
                      keep === row.id ? 'bg-emerald-50/60' : 'hover:bg-slate-50'}`}
                    data-testid={`duplicate-row-${row.id}`}>
              <span data-testid={`duplicate-keep-${row.id}`}
                    aria-pressed={keep === row.id}
                    className={`h-4 w-4 rounded-full border-2 shrink-0 ${
                      keep === row.id ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 truncate">
                  {row.role ? row.role.replace(/_/g, ' ') : 'no office recorded'}
                  {row.status ? ` · ${row.status}` : ''}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {row.email || 'no email'}{row.phone ? ` · ${row.phone}` : ''}
                  {row.created_at ? ` · added ${row.created_at}` : ''}
                </p>
              </div>
              {row.id === group.suggested_keep && (
                <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">
                  Most complete
                </Badge>
              )}
              <span className="text-[11px] text-slate-400">{row.completeness}/9 fields</span>
              {keep === row.id && (
                <Badge className="bg-emerald-600 text-white text-[10px]">Keeping</Badge>
              )}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" disabled={merge.isPending}
                  onClick={() => merge.mutate()} data-testid={`duplicate-merge-${group.church_id}-${group.person_key}`}>
            {merge.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                             : <Merge className="h-3.5 w-3.5 mr-1.5" />}
            Keep this one, merge the rest
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const RecentMerges = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['duplicate-rolls-merged'],
    queryFn: async () => (await api.get('/admin/duplicate-rolls/merged')).data,
  });
  const undo = useMutation({
    mutationFn: async (keepId) => (await api.post(`/admin/duplicate-rolls/undo/${keepId}`)).data,
    onSuccess: () => {
      toast.success('Merge put back — the entries are on the roll again');
      qc.invalidateQueries({ queryKey: ['duplicate-rolls'] });
      qc.invalidateQueries({ queryKey: ['duplicate-rolls-merged'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const merges = (data?.merges || []).filter((m) => m.still_merged > 0);
  if (merges.length === 0) return null;

  return (
    <Card data-testid="recent-merges-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Merged recently</CardTitle>
        <CardDescription>
          Nothing was deleted — put any of these back and the entries return to the roll.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y py-0">
        {merges.map((m) => (
          <div key={m.keep_id} className="flex flex-wrap items-center gap-3 py-2.5"
               data-testid={`merge-record-${m.keep_id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800 truncate">
                {m.name} · {m.church_name}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {m.still_merged} {m.still_merged === 1 ? 'entry' : 'entries'} merged
                {m.roles_kept.length ? ` · offices kept: ${m.roles_kept.join(', ')}` : ''}
                {m.by ? ` · by ${m.by}` : ''}{m.at ? ` · ${m.at.slice(0, 10)}` : ''}
              </p>
            </div>
            <Button size="sm" variant="outline" disabled={undo.isPending}
                    onClick={() => undo.mutate(m.keep_id)}
                    data-testid={`merge-undo-${m.keep_id}`}>
              <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Put back
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const AdminDuplicateRolls = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['duplicate-rolls'],
    queryFn: async () => (await api.get('/admin/duplicate-rolls')).data,
  });
  const groups = data?.groups || [];

  return (
    <DashboardLayout title="Duplicate rolls">
      <div className="p-4 md:p-6 space-y-4 max-w-4xl" data-testid="duplicate-rolls-page">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Duplicate rolls</h1>
          <p className="text-sm text-slate-500 mt-1">
            The same person entered more than once on one roll — usually a second entry made for a
            second office. Counts already treat them as one person; merging tidies the list. Nothing
            is deleted and any merge can be put back.
          </p>
        </div>

        <RecentMerges />

        {isLoading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : groups.length === 0 ? (
          <Card data-testid="duplicate-rolls-empty">
            <CardContent className="py-12 text-center space-y-2">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
              <p className="text-sm text-slate-600">No duplicates found — every roll is clean.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="duplicate-summary">
              <Users className="h-3.5 w-3.5" />
              {groups.length} {groups.length === 1 ? 'person' : 'people'} appear twice or more
              across {data.rows_affected} entries
            </p>
            {groups.map((g) => <Group key={`${g.church_id}-${g.person_key}`} group={g} />)}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDuplicateRolls;
